import type { ItemCategoryTreeNodeDto, ItemCategoryUpdateInput } from '@app/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder, GripVertical, Info, Loader2, Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import type { CSSProperties, DragEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  createItemCategory,
  deleteItemCategory,
  fetchItemCategoryTree,
  reorderItemCategories,
  updateItemCategory,
} from '../hooks/useProcessCartographyApi';
import {
  applyCategoryDrop,
  cloneTree,
  findNode,
  flattenTreeOrder,
  getNextOrderItem,
} from '../utils/itemCategoryTreeDnD';

type DropZone = 'before' | 'after' | 'child';

type BannerState = { type: 'success' | 'error'; text: string } | null;

function sortChildren(nodes: ItemCategoryTreeNodeDto[]): ItemCategoryTreeNodeDto[] {
  return [...nodes]
    .sort(
      (a, b) =>
        a.category.orderItem - b.category.orderItem ||
        a.category.name.localeCompare(b.category.name),
    )
    .map((n) => ({ ...n, children: sortChildren(n.children) }));
}

function StructureCategoryRow({
  node,
  level,
  disabled,
  draggedId,
  dragOverId,
  dropZone,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onAddChild,
  onEdit,
  onDelete,
}: {
  node: ItemCategoryTreeNodeDto;
  level: number;
  disabled: boolean;
  draggedId: string | null;
  dragOverId: string | null;
  dropZone: 'before' | 'after' | 'child' | null;
  onDragStart: (e: DragEvent, categoryId: string) => void;
  onDragOver: (e: DragEvent, categoryId: string) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent, categoryId: string) => void;
  onAddChild: (n: ItemCategoryTreeNodeDto) => void;
  onEdit: (n: ItemCategoryTreeNodeDto) => void;
  onDelete: (id: string, name: string) => void;
}) {
  const { category, children } = node;
  const orderedChildren = sortChildren(children);
  const hasChildren = orderedChildren.length > 0;
  const isDragged = draggedId === category.id;
  const isDragOver = dragOverId === category.id;

  const dropHighlightStyle = (): CSSProperties => {
    if (!isDragOver || !draggedId || draggedId === category.id) return {};
    switch (dropZone) {
      case 'before':
        return {
          borderTop: '3px solid #0284c7',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        };
      case 'after':
        return {
          borderBottom: '3px solid #0284c7',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        };
      case 'child':
        return {
          border: '2px dashed #0284c7',
          backgroundColor: '#e0f2fe',
        };
      default:
        return {};
    }
  };

  return (
    <div className="mb-3" style={{ marginLeft: level * 20 }}>
      {isDragOver && dropZone === 'before' ? (
        <div
          className="mb-2 h-1.5 rounded-md bg-sky-500 shadow-[0_0_10px_rgba(14,165,233,0.45)]"
          aria-hidden
        />
      ) : null}

      <div
        className={`flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 ${
          isDragged ? 'opacity-50' : ''
        }`}
        draggable={!disabled}
        onDragStart={(e) => onDragStart(e, category.id)}
        onDragOver={(e) => onDragOver(e, category.id)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, category.id)}
        style={{ cursor: disabled ? 'default' : 'move', ...dropHighlightStyle() }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div className="shrink-0 cursor-grab text-slate-400 active:cursor-grabbing">
            <GripVertical className="size-4" aria-hidden />
          </div>
          <div
            className="h-4 w-4 shrink-0 rounded-sm border border-slate-200"
            style={{ backgroundColor: category.color }}
            title={category.color}
          />
          {hasChildren ? (
            <Folder className="size-4 shrink-0 text-amber-600" aria-hidden />
          ) : (
            <Tag className="size-4 shrink-0 text-sky-600" aria-hidden />
          )}
          <div className="min-w-0">
            <div className="font-medium text-slate-900">{category.name}</div>
            <div className="text-slate-500 text-xs">{category.active ? 'Actif' : 'Inactif'}</div>
          </div>
        </div>

        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            draggable={false}
            className="inline-flex rounded border border-sky-200 bg-white p-1.5 text-sky-600 hover:bg-sky-50"
            title="Ajouter une sous-catégorie"
            onClick={() => onAddChild(node)}
          >
            <Plus className="size-4" />
          </button>
          <button
            type="button"
            draggable={false}
            className="inline-flex rounded border border-slate-200 bg-white p-1.5 text-slate-700 hover:bg-slate-50"
            title="Modifier la catégorie"
            onClick={() => onEdit(node)}
          >
            <Pencil className="size-4" />
          </button>
          <button
            type="button"
            draggable={false}
            className="inline-flex rounded border border-red-200 bg-white p-1.5 text-red-600 hover:bg-red-50"
            title="Supprimer la catégorie"
            onClick={() => onDelete(category.id, category.name)}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>

      {isDragOver && dropZone === 'after' ? (
        <div
          className="mt-2 h-1.5 rounded-md bg-sky-500 shadow-[0_0_10px_rgba(14,165,233,0.45)]"
          aria-hidden
        />
      ) : null}

      {hasChildren ? (
        <div className="mt-2 ml-4 space-y-3 border-slate-200 border-l-2 pl-4 transition-colors hover:border-sky-400">
          {orderedChildren.map((ch) => (
            <StructureCategoryRow
              key={ch.category.id}
              node={ch}
              level={level + 1}
              disabled={disabled}
              draggedId={draggedId}
              dragOverId={dragOverId}
              dropZone={dropZone}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ItemCategoriesPage() {
  const queryClient = useQueryClient();
  const treeQuery = useQuery({
    queryKey: ['item-categories-tree'],
    queryFn: fetchItemCategoryTree,
  });
  const sortedRoots = sortChildren(treeQuery.data ?? []);

  const [banner, setBanner] = useState<BannerState>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dropZone, setDropZone] = useState<'before' | 'after' | 'child' | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formParentId, setFormParentId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState('#f8f9fa');
  const [formActive, setFormActive] = useState(true);

  useEffect(() => {
    if (!banner) return;
    const t = window.setTimeout(() => setBanner(null), 4500);
    return () => window.clearTimeout(t);
  }, [banner]);

  useEffect(() => {
    const onDragEnd = () => {
      setDraggedId(null);
      setDragOverId(null);
      setDropZone(null);
    };
    window.addEventListener('dragend', onDragEnd);
    return () => window.removeEventListener('dragend', onDragEnd);
  }, []);

  const invalidateTree = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['item-categories-tree'] });
  }, [queryClient]);

  const createMut = useMutation({
    mutationFn: createItemCategory,
    onSuccess: () => {
      invalidateTree();
      setShowModal(false);
      setBanner({ type: 'success', text: 'Catégorie créée.' });
    },
    onError: () => setBanner({ type: 'error', text: 'Création impossible.' }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ItemCategoryUpdateInput }) =>
      updateItemCategory(id, body),
    onSuccess: () => {
      invalidateTree();
      setShowModal(false);
      setBanner({ type: 'success', text: 'Catégorie mise à jour.' });
    },
    onError: () => setBanner({ type: 'error', text: 'Mise à jour impossible.' }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteItemCategory,
    onSuccess: () => {
      invalidateTree();
      setBanner({ type: 'success', text: 'Catégorie supprimée.' });
    },
    onError: () =>
      setBanner({ type: 'error', text: 'Suppression impossible (sous-catégories ?).' }),
  });

  const reorderMut = useMutation({
    mutationFn: reorderItemCategories,
    onSuccess: () => {
      invalidateTree();
      setBanner({ type: 'success', text: 'Ordre enregistré.' });
    },
    onError: () => setBanner({ type: 'error', text: 'Impossible d’enregistrer le déplacement.' }),
  });

  const openCreate = (parentNode: ItemCategoryTreeNodeDto | null) => {
    setIsCreating(true);
    setEditingId(null);
    setFormParentId(parentNode?.category.id ?? null);
    setFormName('');
    setFormColor('#f8f9fa');
    setFormActive(true);
    setShowModal(true);
  };

  const openEdit = (n: ItemCategoryTreeNodeDto) => {
    setIsCreating(false);
    setEditingId(n.category.id);
    setFormParentId(n.category.parentId);
    setFormName(n.category.name);
    setFormColor(n.category.color);
    setFormActive(n.category.active);
    setShowModal(true);
  };

  const handleSaveModal = () => {
    const name = formName.trim();
    if (!name) {
      setBanner({ type: 'error', text: 'Le nom est obligatoire.' });
      return;
    }
    const roots = treeQuery.data ?? [];
    if (isCreating) {
      createMut.mutate({
        name,
        color: formColor,
        active: formActive,
        parentId: formParentId,
        orderItem: getNextOrderItem(roots, formParentId),
      });
    } else if (editingId) {
      updateMut.mutate({ id: editingId, body: { name, color: formColor, active: formActive } });
    }
  };

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(`Supprimer « ${name} » ?`)) return;
    deleteMut.mutate(id);
  };

  const handleDragStart = (e: DragEvent, categoryId: string) => {
    setDraggedId(categoryId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', categoryId);
  };

  const handleDragOver = (e: DragEvent, categoryId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(categoryId);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    if (y < h * 0.2) setDropZone('before');
    else if (y > h * 0.8) setDropZone('after');
    else setDropZone('child');
  };

  const handleDragLeave = () => {
    setDragOverId(null);
    setDropZone(null);
  };

  const handleDrop = (e: DragEvent, targetId: string) => {
    e.preventDefault();
    const roots = treeQuery.data ?? [];
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      setDropZone(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    let dz: DropZone;
    if (y < h * 0.2) dz = 'before';
    else if (y > h * 0.8) dz = 'after';
    else dz = 'child';

    const draggedNode = findNode(roots, draggedId);
    const targetNode = findNode(roots, targetId);
    if (draggedNode && targetNode) {
      const nextRoots = cloneTree(roots);
      const ok = applyCategoryDrop(nextRoots, draggedId, targetId, dz);
      if (!ok) {
        setBanner({ type: 'error', text: 'Déplacement invalide.' });
      } else {
        reorderMut.mutate({ items: flattenTreeOrder(nextRoots) });
      }
    }
    setDraggedId(null);
    setDragOverId(null);
    setDropZone(null);
  };

  const busy =
    treeQuery.isFetching ||
    createMut.isPending ||
    updateMut.isPending ||
    deleteMut.isPending ||
    reorderMut.isPending;

  if (treeQuery.isLoading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-3 text-slate-600">
        <Loader2 className="size-10 animate-spin text-sky-600" />
        <p>Chargement des données…</p>
      </div>
    );
  }

  if (treeQuery.isError) {
    return (
      <div className="m-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
        Impossible de charger la structure des processus.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      {banner ? (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            banner.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-red-200 bg-red-50 text-red-900'
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-slate-900 text-xl tracking-tight">
            Structure des Processus
          </h1>
          <p className="mt-1 text-slate-600 text-sm">
            Gérez les catégories d’items et leur hiérarchie.
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 font-medium text-sm text-white shadow-sm hover:bg-sky-700 disabled:opacity-50"
          disabled={busy}
          onClick={() => openCreate(null)}
        >
          <Plus className="size-4" />
          Nouvelle catégorie
        </button>
      </header>

      <div className="mb-6 rounded-lg border border-sky-200 bg-sky-50 p-4 text-slate-800 text-sm">
        <div className="mb-2 flex items-center gap-2 font-medium text-slate-900">
          <Info className="size-4 text-sky-600" />
          Comment réorganiser les catégories :
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <strong className="text-slate-900">Changer l’ordre</strong>
            <p className="mt-1 text-slate-600">
              Glissez vers le <span className="text-sky-700">haut</span> ou le{' '}
              <span className="text-sky-700">bas</span> d’une ligne.
            </p>
          </div>
          <div>
            <strong className="text-slate-900">Créer un enfant</strong>
            <p className="mt-1 text-slate-600">
              Glissez vers le <span className="text-sky-700">centre</span> d’une catégorie.
            </p>
          </div>
          <div>
            <strong className="text-slate-900">Changer de parent</strong>
            <p className="mt-1 text-slate-600">
              Glissez vers une autre catégorie du même niveau ou imbriquée.
            </p>
          </div>
        </div>
      </div>

      <div className="py-2">
        {sortedRoots.map((n) => (
          <StructureCategoryRow
            key={n.category.id}
            node={n}
            level={0}
            disabled={reorderMut.isPending}
            draggedId={draggedId}
            dragOverId={dragOverId}
            dropZone={dropZone}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onAddChild={(node) => openCreate(node)}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {showModal ? (
        <dialog
          open
          className="fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-black/50 p-4 [&::backdrop]:bg-transparent"
          aria-labelledby="structure-cat-modal-title"
          onClick={() => setShowModal(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setShowModal(false);
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-lg"
            onClick={(ev) => ev.stopPropagation()}
            onKeyDown={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-center justify-between border-slate-100 border-b px-4 py-3">
              <h2 id="structure-cat-modal-title" className="font-semibold text-slate-900">
                {isCreating ? 'Créer une nouvelle catégorie' : 'Modifier la catégorie'}
              </h2>
              <button
                type="button"
                className="rounded p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Fermer"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="space-y-4 p-4">
              <div>
                <label className="block text-slate-600 text-xs" htmlFor="structure-cat-name">
                  Nom de la catégorie
                </label>
                <input
                  id="structure-cat-name"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={formName}
                  onChange={(ev) => setFormName(ev.target.value)}
                  placeholder="Nom"
                />
              </div>
              <div>
                <label className="block text-slate-600 text-xs" htmlFor="structure-cat-color">
                  Couleur
                </label>
                <input
                  id="structure-cat-color"
                  type="color"
                  className="mt-2 h-10 w-full max-w-[4rem] cursor-pointer rounded border border-slate-200 bg-white"
                  value={formColor}
                  onChange={(ev) => setFormColor(ev.target.value)}
                />
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formActive}
                  onChange={(ev) => setFormActive(ev.target.checked)}
                />
                Catégorie active
              </label>
            </div>
            <div className="flex justify-end gap-2 border-slate-100 border-t px-4 py-3">
              <button
                type="button"
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800 text-sm hover:bg-slate-50"
                onClick={() => setShowModal(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="rounded-lg bg-sky-600 px-3 py-2 font-medium text-sm text-white hover:bg-sky-700 disabled:opacity-50"
                disabled={createMut.isPending || updateMut.isPending}
                onClick={handleSaveModal}
              >
                {isCreating ? 'Créer' : 'Modifier'}
              </button>
            </div>
          </div>
        </dialog>
      ) : null}
    </div>
  );
}
